export const COUNTRIES = [
  { code: "MX", label: "México (+52)", prefix: "+52" },
  { code: "US", label: "EE.UU. (+1)", prefix: "+1" },
];

export const FIELD_LABELS = {
  name: "Nombre",
  phone: "Teléfono",
  userEmail: "Email",
  topic: "Tema",
  notes: "Notas",
  origin: "Origen",
};

export const EMPTY_LEAD = {
  name: "",
  phone: "",
  userEmail: "",
  topic: "",
  notes: "",
  origin: "",
  country: "MX",
};

export function formatDateOnly(date) {
  if (!date) return "-";
  try {
    return date.toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "America/Mexico_City",
    });
  } catch {
    return "-";
  }
}

export function formatDateRange(start, end) {
  const s = start instanceof Date ? start : start?.toDate ? start.toDate() : new Date(start);
  const e = end instanceof Date ? end : end?.toDate ? end.toDate() : new Date(end);
  if (isNaN(s.getTime())) return "-";
  const opts = {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Mexico_City",
  };
  return `${s.toLocaleDateString("es-MX", opts)} — ${
    isNaN(e.getTime()) ? "" : e.toLocaleDateString("es-MX", opts)
  }`;
}

export const fmtMXN = (v) => {
  const str = String(v).replace(/,/g, "");
  const n = Number(str);
  if (isNaN(n)) return "";
  if (str.endsWith(".")) return n.toLocaleString("es-MX") + ".";
  const decMatch = str.match(/\.(\d{1,2})$/);
  if (decMatch) {
    return n.toLocaleString("es-MX", {
      minimumFractionDigits: decMatch[1].length,
      maximumFractionDigits: 2,
    });
  }
  return n.toLocaleString("es-MX");
};

export const fmtMXNSigned = (v) => {
  const str = String(v).replace(/,/g, "");
  if (str === "-" || str === "-.") return str;
  const n = Number(str);
  if (isNaN(n)) return "";
  const sign = str.startsWith("-") ? "-" : "";
  const abs = Math.abs(n);
  if (str.endsWith(".")) return sign + abs.toLocaleString("es-MX") + ".";
  const decMatch = str.match(/\.(\d{1,2})$/);
  if (decMatch) {
    return (
      sign +
      abs.toLocaleString("es-MX", {
        minimumFractionDigits: decMatch[1].length,
        maximumFractionDigits: 2,
      })
    );
  }
  return sign + abs.toLocaleString("es-MX");
};

const ORIGIN_COLORS = [
  "bg-purple-100 text-purple-700",
  "bg-pink-100 text-pink-700",
  "bg-indigo-100 text-indigo-700",
  "bg-teal-100 text-teal-700",
  "bg-orange-100 text-orange-700",
  "bg-cyan-100 text-cyan-700",
  "bg-rose-100 text-rose-700",
  "bg-amber-100 text-amber-700",
];

/* Un lead capturado a mano al que nadie le puso origen. Se escribe explícito
   en vez de dejar el campo vacío: ausente significa "llegó por el formulario
   público" (las rules solo dejan poner `origin` a owner/admin, así que el
   homesite siempre crea sin él), y un alta manual en blanco se contaba como
   Venueboard — inflando el % de contacto de Analíticas web con leads que
   nunca vinieron de la web. */
export const ORIGEN_SIN_ASIGNAR = "Sin asignar";

export const esSinAsignar = (origin) =>
  String(origin || "").trim().toLowerCase() === ORIGEN_SIN_ASIGNAR.toLowerCase();

/* Lead que llegó por el formulario público de Venueboard (vs. capturado a
   mano con otro origen). Misma heurística que originColor: origin vacío o
   "venueboard". La usan AdminStats y el % de contacto de Analíticas web. */
export function isContactFormLead(origin) {
  if (esSinAsignar(origin)) return false;
  return !origin || String(origin).trim().toLowerCase() === "venueboard";
}

export function originColor(origin) {
  // Sin asignar es ausencia de dato, no un origen: gris, no un color propio.
  if (esSinAsignar(origin)) return "bg-zinc-100 text-zinc-500";
  if (!origin || origin.toLowerCase() === "venueboard") return "bg-[#1b4d2e1a] text-[#1b4d2e]";
  let hash = 0;
  for (let i = 0; i < origin.length; i++) hash = (hash * 31 + origin.charCodeAt(i)) | 0;
  return ORIGIN_COLORS[Math.abs(hash) % ORIGIN_COLORS.length];
}

/* Accent-insensitive lowercase form used for search matching */
export function normalizeText(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/* Total of a lead's desglose (units × unitPrice, +16% IVA when enabled) */
export function leadDesgloseTotal(form) {
  const subtotal = (form?.desglose || []).reduce(
    (s, i) => s + (Number(i.units) * Number(i.unitPrice) || 0),
    0
  );
  return form?.ivaEnabled ? subtotal * 1.16 : subtotal;
}

/* "hace 3 días" / "ayer" / "hace 2 h" in es-MX */
export function relativeTime(date) {
  const d = date instanceof Date ? date : date?.toDate ? date.toDate() : new Date(date);
  if (!d || isNaN(d.getTime())) return "-";
  const rtf = new Intl.RelativeTimeFormat("es-MX", { numeric: "auto" });
  const minutes = Math.round((Date.now() - d.getTime()) / 60000);
  if (minutes < 1) return "ahora";
  if (minutes < 60) return rtf.format(-minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (hours < 24) return rtf.format(-hours, "hour");
  const days = Math.round(hours / 24);
  if (days < 30) return rtf.format(-days, "day");
  const months = Math.round(days / 30);
  if (months < 12) return rtf.format(-months, "month");
  return rtf.format(-Math.round(months / 12), "year");
}

export function parsePhone(phone) {
  if (!phone) return { country: "MX", number: "" };
  const trimmed = phone.trim();
  if (trimmed.startsWith("+1 ")) return { country: "US", number: trimmed.slice(3) };
  if (trimmed.startsWith("+52 ")) return { country: "MX", number: trimmed.slice(4) };
  if (trimmed.startsWith("+1")) return { country: "US", number: trimmed.slice(2) };
  if (trimmed.startsWith("+52")) return { country: "MX", number: trimmed.slice(3) };
  return { country: "MX", number: trimmed };
}

export function generateId() {
  return Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
}

// ¿Las fechas de interés de este lead siguen "vivas"? Un lead eliminado
// (soft delete), convertido a evento (ya bloquea como evento, no como
// interés) o perdido ya no apartó nada: no pinta el calendario de leads ni
// cuenta como conflicto para otros leads. Es la ÚNICA regla; el calendario
// y la detección de conflictos la leen de aquí para no volver a divergir.
export function leadConFechasVivas(form) {
  if (!form || form.deleted) return false;
  if (form.convertedToEventId) return false;
  if ((form.status || "pendiente") === "perdido") return false;
  return Array.isArray(form.interestDates) && form.interestDates.length > 0;
}
